'use client'

import { useState } from 'react'

import {
  Option,
  MultiSelect as UIMultiSelect
} from '@/components/ui/multi-select'

import { isFieldInvalid } from '@/utils'

import { FieldWrapper } from './field-wrapper'
import { useFieldContext } from './form'

interface MultiSelectProps {
  'aria-label'?: string
  asyncSearch?: boolean
  className?: string
  disabled?: boolean
  isLoading?: boolean
  label?: string
  maxCount?: number
  options: Option[]
  placeholder?: string
  required?: boolean
  searchable?: boolean
  searchParam?: string
  variant?: 'default' | 'inverted' | 'secondary'
}

export const MultiSelect = ({
  'aria-label': ariaLabel,
  asyncSearch = false,
  className,
  disabled,
  isLoading,
  label,
  maxCount,
  options,
  placeholder,
  required = false,
  searchable,
  searchParam = 'search-options',
  variant
}: MultiSelectProps) => {
  const field = useFieldContext<string[]>()
  const isInvalid = isFieldInvalid(field)

  // Ensure we're passing the current field value, not just initial default
  const fieldValue = field.state.value || []

  const [hasInitialData, setHasInitialData] = useState(false)

  if (fieldValue.length > 0 && !hasInitialData) {
    setHasInitialData(true)
  }

  // Use a key that changes when data first loads to force UIMultiSelect to remount with correct values
  // After initial load, keep the same key to avoid unnecessary remounts during user interaction
  const componentKey = hasInitialData ? 'loaded' : 'empty'

  return (
    <FieldWrapper field={field} label={label} required={required}>
      <UIMultiSelect
        aria-label={ariaLabel}
        asyncSearch={asyncSearch}
        className={className}
        data-invalid={isInvalid || undefined}
        defaultValue={fieldValue}
        disabled={disabled || field.state.meta.isValidating || isLoading}
        isLoading={isLoading}
        key={componentKey}
        maxCount={maxCount}
        onValueChange={(values) => field.handleChange(values)}
        options={options}
        placeholder={placeholder}
        searchable={searchable}
        searchParam={searchParam}
        variant={variant}
      />
    </FieldWrapper>
  )
}
